import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { XOctagon } from "lucide-react";

const FILTERS = ["", "QUEUED", "PROCESSING", "COMPLETED", "CANCELLED", "FAILED"];
const FILTER_LABEL = { "": "Todos", QUEUED: "En cola", PROCESSING: "Procesando", COMPLETED: "Completados", CANCELLED: "Cancelados", FAILED: "Fallidos" };

export default function Jobs() {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const qc = useQueryClient();

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs", filter],
    queryFn: async () => (await api.get("/admin/jobs", { params: { status: filter } })).data,
    refetchInterval: 4000,
  });

  const { data: job } = useQuery({
    queryKey: ["job", selected],
    queryFn: async () => (await api.get(`/admin/jobs/${selected}`)).data,
    enabled: !!selected,
  });

  const cancel = async (id) => {
    try {
      await api.post(`/admin/jobs/${id}/cancel`);
      toast.success("Cancelación solicitada");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job", id] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const ACTIVE = ["QUEUED", "PROCESSING", "ANALYZING", "OCR", "DETECTING", "RECONSTRUCTING", "EXPORTING"];

  return (
    <Layout>
      <PageHeader title="Cola de Trabajos" subtitle="Monitor en vivo de reconstrucción por capas" />
      <div className="p-8">
        <div className="flex gap-2 mb-5 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`job-filter-${f || "all"}`} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                filter === f ? "text-white" : "text-slate-400 hover:text-slate-200"}`}
              style={filter === f ? { backgroundColor: "#C2620E" } : { backgroundColor: "#111827" }}>
              {FILTER_LABEL[f]}
            </button>
          ))}
        </div>

        <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#1E293B" }}>
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead style={{ backgroundColor: "#0D111A" }}>
              <tr className="text-left text-xs text-slate-500 uppercase">
                <th className="px-4 py-3">Job ID</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Operación</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Progreso</th>
                <th className="px-4 py-3">Créditos</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} data-testid={`job-row-${j.job_id}`}
                  onClick={() => setSelected(j.job_id)}
                  className="border-t hover:bg-[#0D111A] transition-colors cursor-pointer" style={{ borderColor: "#1E293B" }}>
                  <td className="px-4 py-3 font-mono text-xs text-red-400">{j.job_id}</td>
                  <td className="px-4 py-3 font-mono text-slate-300">{j.telegram_id}</td>
                  <td className="px-4 py-3 text-slate-400">{j.operation}</td>
                  <td className="px-4 py-3"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-3">
                    <div className="w-24 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full" style={{ width: `${j.progress || 0}%`, backgroundColor: "#C2620E" }} />
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-amber-400">{j.used_credits || j.reserved_credits}</td>
                  <td className="px-4 py-3">
                    {ACTIVE.includes(j.status) && (
                      <Button data-testid={`cancel-job-btn-${j.job_id}`} size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); cancel(j.job_id); }} className="text-rose-400 hover:text-rose-300 h-7">
                        <XOctagon className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Sin trabajos</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[#0D111A] border-l border-[#1E293B] text-slate-100 overflow-y-auto w-full sm:max-w-lg" data-testid="job-detail-sheet">
          <SheetHeader><SheetTitle className="text-slate-100 font-heading font-mono">{job?.job_id}</SheetTitle>
            <SheetDescription className="text-slate-500">Detalle del trabajo de reconstrucción.</SheetDescription></SheetHeader>
          {job && (
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex items-center gap-2"><StatusBadge status={job.status} /><span className="text-slate-500">{job.progress}%</span></div>
              {[
                ["Usuario", job.telegram_id], ["Archivo", job.filename], ["Operación", job.operation],
                ["Costo", job.cost], ["Créditos usados", job.used_credits], ["Capas", job.layer_count || "—"],
                ["Creado", job.created_at?.slice(0, 19)], ["Duración (s)", job.duration ? Math.round(job.duration) : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b py-2" style={{ borderColor: "#1E293B" }}>
                  <span className="text-slate-500">{k}</span><span className="font-mono text-slate-200">{String(v)}</span>
                </div>
              ))}
              {job.error && (
                <div className="rounded-lg border p-3 mt-2" style={{ borderColor: "#7F1D1D", backgroundColor: "rgba(239,68,68,0.1)" }}>
                  <div className="text-xs text-rose-400 mb-1">Error</div>
                  <div className="font-mono text-xs text-rose-300 break-all">{job.error}</div>
                </div>
              )}
              {job.status === "COMPLETED" && (
                <Link to={`/editor/${job.job_id}`} data-testid="open-editor-btn"
                  className="block w-full text-center bg-[#C2620E] hover:bg-[#FF8A2B] text-white rounded-md py-2 mt-2 text-sm font-medium">
                  ✏️ Abrir editor de capas
                </Link>
              )}
              {ACTIVE.includes(job.status) && (
                <Button data-testid="detail-cancel-job-btn" onClick={() => cancel(job.job_id)}
                  className="w-full bg-rose-600 hover:bg-rose-500 text-white mt-2">Cancelar trabajo</Button>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
